import {Card, Icon} from '@atoms';
import {H1} from '@quarks';
import {Canvas, Fullbody, Heading} from '@templates';
import {useTranslation} from 'react-i18next';
import {Avatar} from '@molecules';
import {Navigate, useNavigate, useParams} from 'react-router-dom';
import {BreadCrumbs, useConnectBreadCrumbs} from './BreadCrumbs';
import {UserSchedule} from './components/UserSchedule';
import {useCallback, useEffect, useMemo} from 'react';
import {
  actions,
  getConnectedUserById,
  getConnectsWorkdaysByUserId,
  getConnectsWorkspaceReservationsByUserId,
  getGroupById,
  isLoadingConnectWorkspaceReservationsByUserId,
  useAppDispatch,
  useAppSelector,
} from '@lib/store';
import {GROUP_DATA_DATE_RANGE} from '@lib/store/src/data/groups/constants';

// Custom hook for user resolution logic
const useUserResolution = (groupId: string | undefined, userId: string | undefined) => {
  const group = useAppSelector((state) => getGroupById(state, groupId || ''));
  const connection = useAppSelector((state) => getConnectedUserById(state, userId || ''));

  return useMemo(() => {
    const user = connection || group?.members.find((u) => u.user.id === userId)?.user;
    return {group, user};
  }, [connection, group, userId]);
};

export const ConnectDetailPage = () => {
  const {groupId, userId} = useParams<{groupId?: string; userId?: string}>();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {paths} = useConnectBreadCrumbs();
  const goBackPath = paths.at(-2) ?? '/connect';

  // Use custom hook for user resolution
  const {user} = useUserResolution(groupId, userId);

  // Memoize selectors to prevent unnecessary re-renders
  const isLoading = useAppSelector((state) => isLoadingConnectWorkspaceReservationsByUserId(state, userId!));

  // Combined and optimized data loading effect
  useEffect(() => {
    if (!userId || isLoading) return;

    dispatch(
      actions.loadConnectWorkspaceReservations({
        userId,
        range: GROUP_DATA_DATE_RANGE,
      }),
    );
    dispatch(
      actions.loadConnectWorkdays({
        userId,
        range: GROUP_DATA_DATE_RANGE,
      }),
    );
  }, [dispatch, userId, isLoading]);

  // Early return for missing user
  if (!user) {
    return (
      <Navigate
        to={goBackPath}
        replace
      />
    );
  }

  return (
    <>
      <Heading className={'px-4 bg-beige-200 border-none justify-between'}>
        <div className={'col-span-full flex w-full grid-cols-2 justify-between items-center'}>
          <button onClick={() => navigate(goBackPath)}>
            <Icon
              icon="caretLeft"
              className="size-5"
            />
          </button>

          <h4>{t('groups:ConnectedCoworker')}</h4>

          <span className="size-5" />
        </div>
      </Heading>

      <Canvas />

      <Fullbody className="p-4 lg:p-8 md:py-14">
        <div className="md:flex justify-center flex-1">
          <div className="grid gap-4 sm:gap-[var(--gutterWidth)] grid-cols-1 md:grid-cols-[minmax(0,var(--maxWidth))] justify-center">
            <BreadCrumbs className="hidden sm:flex" />

            <div className="grid gap-4 sm:gap-[var(--gutterWidth)] grid-cols-[minmax(0,var(--mainWidth))] col-span-full justify-center">
              <Card className="bg-beige-200 py-8 px-6 grid grid-cols-[auto_1fr_auto] items-center gap-4">
                <Avatar
                  user={user}
                  size="xlarge"
                />
                <div className="flex flex-col gap-1 items-center sm:items-start">
                  <H1 className="col-span-full">{user.name}</H1>
                </div>
              </Card>

              <div className={'grid w-full gap-4 sm:gap-[var(--gutterWidth)] md:col-start-1 order-1'}>
                <UserSchedule user={user} />
              </div>
            </div>
          </div>
        </div>
      </Fullbody>
    </>
  );
};
